<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { faMessageSmile } from '@fortawesome/pro-solid-svg-icons'
import { KsButton } from '@aschehoug/kloss'
import { pageTitle } from '~/utils/dom'
import useAuthStore from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import useColorPair from '~/composables/useColorPair'
import AuTooltip from '~/components/utils/AuTooltip.vue'

const props = withDefaults(defineProps<{
  variant?: string
  size?: string
  shape?: string
  icon?: IconDefinition
  classes?: string
  target?: string
  label?: boolean
  styles?: Record<string, string | number>
  side?: string
}>(), {
  variant: 'primary',
  size: 'large',
  shape: 'square',
  icon: () => faMessageSmile as IconDefinition,
  classes: 'relative',
  target: '_blank',
  label: false,
  styles: () => ({}),
  side: 'top',
})

const { t } = useI18n()
const route = useRoute()
const { isTeacher } = storeToRefs(useAuthStore())
const { activeColorPair } = useColorPair()
const { userOrganization } = storeToRefs(useAuthStore())

const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL
const newLine = '%0D%0A%0D%0A'

const showFeedbackButton = computed(() => isTeacher.value)

const subject = computed(() => t('reportFeedback.subject', { title: encodeURIComponent(pageTitle.value) }))
const bodyUrl = computed(() => t('reportFeedback.url', { url: encodeURIComponent(location.origin + route.fullPath) }))
const organizationMessage = computed(() => userOrganization.value ? t('reportFeedback.organization', { name: userOrganization.value.name, number: userOrganization.value.number }) : '')
const body = computed(() => `${t('reportFeedback.intro')}${newLine}${newLine}${t('reportFeedback.placeholder')}${newLine}${newLine}${bodyUrl.value}${newLine}${organizationMessage.value}${newLine}`)
const mailtoLink = computed(() => `mailto:${supportEmail}?subject=${subject.value}&body=${body.value}`)

const openLink = () => window?.open(mailtoLink.value, '_blank')?.focus()

const buttonStyle = computed(() => {
  const styles = activeColorPair.value?.value
    ? {
        background: activeColorPair.value.value.text.rgb,
        color: activeColorPair.value.value.background.rgb,
        border: `1.5px solid ${activeColorPair.value.value.background.rgb}`
      }
    : {}

  Object.assign(styles, props.styles)
  return styles
})
</script>

<template>
  <div v-if="showFeedbackButton" class="relative w-fit content-center">
    <AuTooltip
      :disabled="props.label"
      :side="props.side"
      :content="t('reportFeedback.button')"
    >
      <KsButton
        :class="props.classes"
        :variant="props.variant"
        :icon-left="props.icon"
        :shape="props.shape"
        :size="props.size"
        :target="props.target"
        :href="mailtoLink"
        :data-pendo="PendoFeature.Feedback.Button"
        :style="buttonStyle"
        @click="openLink"
      >
        <span v-if="props.label" v-text="t('reportFeedback.button')" />
      </KsButton>
    </AuTooltip>
  </div>
</template>
